import { useCallback, useMemo, useState } from 'react'
import { getUserDisplayName } from '../apps/professional-app/fa/common/aifeatures/utils'
import { type SelectOption } from '../components/SelectDropdown/SelectDropdown'
import { type ShareType, type User, type UserShareSettings } from '../models/User'
import { selectSafeAuthenticatedUser } from '../store/auth/authSlice'
import { useAppSelector } from '../store/hooks'
import { useGetAllUserSharesQuery, useGetUsersQuery } from '../store/user/user'

const PAGE_SIZE = 150

export const filterSharedUsers = (currentUserId: string, users: User[], shares: UserShareSettings[]): User[] => {
  const sharedOwnerIds = new Set(shares.map((share) => share.ownerUserId))
  return users
    .filter((user) => user.id === currentUserId || sharedOwnerIds.has(user.id))
    .sort((a, b) => (a.id === currentUserId ? -1 : b.id === currentUserId ? 1 : getUserDisplayName(a).localeCompare(getUserDisplayName(b))))
}

export const toSharedUserOption = (user: User, currentUserId: string): SelectOption => {
  const displayName = getUserDisplayName(user)
  return { id: user.id, label: user.id === currentUserId ? `${displayName} (me)` : displayName }
}

// Users sharing the given resource type with the current user, plus the current user (always
// first). A non-empty search term switches the users query to server-side name search; the
// share filter still applies to the results.
export const useSharedUsers = ({ shareType }: { shareType: ShareType }) => {
  const currentUser = useAppSelector(selectSafeAuthenticatedUser)
  const [searchTerm, setSearchTerm] = useState('')

  const { data: shares, isFetching: sharesFetching } = useGetAllUserSharesQuery({
    accountId: currentUser.accountId,
    userId: currentUser.id,
    shareType,
  })

  const sharingWithMeUserIds = shares?.data.slice(0, PAGE_SIZE).map((share) => share.ownerUserId)
  const ids = sharingWithMeUserIds != null ? [...sharingWithMeUserIds, currentUser.id] : undefined

  const { data: usersData, isFetching: usersFetching } = useGetUsersQuery(
    {
      accountId: currentUser.accountId,
      identityTypes: ['idp'],
      name: searchTerm.trim() || undefined,
      ids: searchTerm.trim() ? undefined : ids,
      pageRequest: { page: 0, size: PAGE_SIZE + 1 },
    },
    { refetchOnMountOrArgChange: true, skip: shares == null },
  )

  const users = useMemo(() => filterSharedUsers(currentUser.id, usersData?.data ?? [], shares?.data ?? []), [currentUser.id, usersData, shares])

  // The full set of valid shared user ids (current user included), independent of the search
  // term — undefined until the shares have loaded.
  const sharedUserIds = useMemo(
    () => (shares == null ? undefined : new Set([currentUser.id, ...shares.data.map((share) => share.ownerUserId)])),
    [shares, currentUser.id],
  )

  return { users, currentUser, sharedUserIds, isFetching: usersFetching || sharesFetching, searchTerm, setSearchTerm }
}

// Select-ready view of useSharedUsers: options labeled with a "(me)" suffix for the current
// user, plus the loading/search props SelectDropdown expects and a resolver back to the User.
export const useSharedUserOptions = ({ shareType }: { shareType: ShareType }) => {
  const { users, currentUser, sharedUserIds, isFetching, setSearchTerm } = useSharedUsers({ shareType })

  const toOption = useCallback((user: User): SelectOption => toSharedUserOption(user, currentUser.id), [currentUser.id])

  const options = useMemo(() => users.map(toOption), [users, toOption])
  const findUser = useCallback((option: SelectOption) => users.find((user) => user.id === option.id), [users])
  const onSearchInputChange = useCallback(async (term: string) => setSearchTerm(term), [setSearchTerm])

  return { options, currentUser, sharedUserIds, toOption, findUser, loading: isFetching, onSearchInputChange }
}
